import React, { useState } from "react";
import {
  addDoc, arrayUnion, collection, doc, getDoc, getDocs, query, setDoc, Timestamp, updateDoc, where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../AuthContext";
import { OAKDALE_BARS, IMPORT_GATHERED_ON } from "./oakdaleImportData";

// Admin-only: load bars + the specials/events they post publicly online.
// Step 1 "Find on Google" matches each bar to its Google listing (same as
// Add a Bar). Step 2 "Import checked" writes them. Everything imported is
// tagged source:"web", verified:false, with the page it came from.
// Running it twice is safe: anything already there is skipped.

const DAY_LABEL = { Mon: "Mon", Tue: "Tue", Wed: "Wed", Thu: "Thu", Fri: "Fri", Sat: "Sat", Sun: "Sun" };

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const p = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, "0")}${p}` : `${h12}${p}`;
}

function whenSpecial(s) {
  const days = s.recurrence === "daily" ? "Every day" : (s.days || []).map((d) => DAY_LABEL[d] || d).join(", ");
  return `${days}${s.startTime ? ` · ${formatTime(s.startTime)}–${formatTime(s.endTime)}` : ""}`;
}

function whenEvent(e) {
  return `${e.date ? e.date : `Every ${e.dayOfWeek}`} · ${formatTime(e.time)}`;
}

function findOnGoogle(text) {
  return new Promise((resolve) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      resolve({ error: "Google Maps didn't load. Refresh the page." });
      return;
    }
    const service = new window.google.maps.places.PlacesService(document.createElement("div"));
    service.textSearch({ query: text }, (places, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !places || !places.length) {
        resolve({ error: "No Google match found." });
        return;
      }
      const p = places[0];
      resolve({
        placeId: p.place_id,
        name: p.name,
        address: p.formatted_address,
        lat: p.geometry?.location ? p.geometry.location.lat() : null,
        lng: p.geometry?.location ? p.geometry.location.lng() : null,
      });
    });
  });
}

const box = { background: "#17131F", borderRadius: "14px", padding: "18px", marginBottom: "16px" };
const btn = {
  minHeight: "44px", padding: "0 18px", borderRadius: "10px", border: "none",
  font: "700 15px Inter, sans-serif", cursor: "pointer",
};

export default function AdminImport() {
  const { user, role } = useAuth();
  const [matches, setMatches] = useState({}); // key -> google match
  const [checked, setChecked] = useState(() => {
    const c = {};
    OAKDALE_BARS.forEach((b) => {
      c[b.key] = true;
      b.specials.forEach((_, i) => { c[`${b.key}:s${i}`] = true; });
      b.events.forEach((_, i) => { c[`${b.key}:e${i}`] = true; });
    });
    return c;
  });
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  if (role !== "admin") {
    return <p style={{ color: "#fff" }}>Admins only.</p>;
  }

  const toggle = (k) => setChecked((prev) => ({ ...prev, [k]: !prev[k] }));
  const addLog = (line) => setLog((prev) => [...prev, line]);

  const findAll = async () => {
    setBusy(true);
    const found = {};
    for (const bar of OAKDALE_BARS) {
      found[bar.key] = await findOnGoogle(`${bar.name} ${bar.address}`);
    }
    setMatches(found);
    setBusy(false);
  };

  const importChecked = async () => {
    setBusy(true);
    setLog([]);
    const stamp = Timestamp.now();
    for (const bar of OAKDALE_BARS) {
      if (!checked[bar.key]) continue;
      const m = matches[bar.key];
      if (!m || !m.placeId) {
        addLog(`Skipped ${bar.name}: no Google match.`);
        continue;
      }
      try {
        const barRef = doc(db, "bars", m.placeId);
        const snap = await getDoc(barRef);
        if (!snap.exists()) {
          await setDoc(barRef, {
            name: m.name || bar.name,
            address: m.address || bar.address,
            lat: m.lat,
            lng: m.lng,
            state: "MN",
            zip: "55128",
            phone: bar.phone,
            website: bar.website,
            photos: [],
            amenities: bar.amenities,
            specials: [],
            birthdayDeals: [],
            ownerId: "",
            charityId: "",
            source: "web",
            importedAt: stamp,
          });
          addLog(`Added ${bar.name}.`);
        } else {
          const existing = snap.data();
          const patch = {};
          if (!existing.phone && bar.phone) patch.phone = bar.phone;
          if (!existing.website && bar.website) patch.website = bar.website;
          if ((existing.lat == null || existing.lng == null) && m.lat != null) {
            patch.lat = m.lat;
            patch.lng = m.lng;
          }
          if (bar.amenities.length) patch.amenities = arrayUnion(...bar.amenities);
          if (Object.keys(patch).length) await updateDoc(barRef, patch);
          addLog(`${bar.name} was already in the app; filled in anything missing.`);
        }

        // Specials (stored on the bar doc), skipping titles already there.
        const current = ((await getDoc(barRef)).data()?.specials) || [];
        const haveTitles = new Set(current.map((s) => (s.title || "").toLowerCase()));
        const newSpecials = [];
        bar.specials.forEach((s, i) => {
          if (!checked[`${bar.key}:s${i}`] || haveTitles.has(s.title.toLowerCase())) return;
          newSpecials.push({
            id: Date.now() + i,
            title: s.title,
            category: s.category,
            recurrence: s.recurrence,
            days: s.days || [],
            date: s.date || "",
            startTime: s.startTime || "",
            endTime: s.endTime || "",
            details: s.details || "",
            source: "web",
            sourceUrl: s.sourceUrl,
            verified: false,
            gatheredOn: IMPORT_GATHERED_ON,
          });
        });
        if (newSpecials.length) {
          await updateDoc(barRef, { specials: [...current, ...newSpecials] });
          addLog(`  + ${newSpecials.length} special(s)`);
        }

        // Events (own collection), skipping same title + day/date + time.
        const evSnap = await getDocs(query(collection(db, "events"), where("barId", "==", m.placeId)));
        const haveEvents = new Set(
          evSnap.docs.map((d) => {
            const e = d.data();
            return `${(e.title || "").toLowerCase()}|${e.dayOfWeek || e.date}|${e.time}`;
          })
        );
        let added = 0;
        for (let i = 0; i < bar.events.length; i++) {
          const e = bar.events[i];
          if (!checked[`${bar.key}:e${i}`]) continue;
          const sig = `${e.title.toLowerCase()}|${e.dayOfWeek || e.date}|${e.time}`;
          if (haveEvents.has(sig)) continue;
          await addDoc(collection(db, "events"), {
            barId: m.placeId,
            title: e.title,
            category: e.category,
            subcategories: e.subcategories || [],
            recurrence: e.date ? "one-time" : "weekly",
            dayOfWeek: e.date ? "" : e.dayOfWeek,
            date: e.date || "",
            time: e.time,
            details: e.details || "",
            charityName: "",
            createdBy: user?.uid || "",
            createdByRole: "admin",
            createdAt: stamp,
            source: "web",
            sourceUrl: e.sourceUrl,
            verified: false,
            gatheredOn: IMPORT_GATHERED_ON,
          });
          added++;
        }
        if (added) addLog(`  + ${added} event(s)`);
      } catch (err) {
        console.error(err);
        addLog(`Error on ${bar.name}: ${err.message}`);
      }
    }
    addLog("Done.");
    setBusy(false);
  };

  const matched = Object.keys(matches).length > 0;

  return (
    <div style={{ color: "#fff", maxWidth: "900px" }}>
      <h2 style={{ margin: "0 0 6px" }}>Import Oakdale bars</h2>
      <p style={{ color: "#B8B0C8", margin: "0 0 18px", fontSize: "16px" }}>
        Found online on {IMPORT_GATHERED_ON} from each bar's own website and mnbarbingo.com. Uncheck anything you
        don't want. Imported items are marked unverified until the owner confirms them.
      </p>

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
        <button type="button" onClick={findAll} disabled={busy} style={{ ...btn, background: "#8B5CF6", color: "#fff", opacity: busy ? 0.6 : 1 }}>
          1. Find on Google
        </button>
        <button type="button" onClick={importChecked} disabled={busy || !matched} style={{ ...btn, background: "#FF3D81", color: "#0B0B12", opacity: busy || !matched ? 0.5 : 1 }}>
          2. Import checked
        </button>
      </div>

      {log.length > 0 && (
        <div style={{ ...box, background: "#0D0B1A", fontFamily: "monospace", fontSize: "14px", whiteSpace: "pre-wrap" }}>
          {log.join("\n")}
        </div>
      )}

      {OAKDALE_BARS.map((bar) => {
        const m = matches[bar.key];
        return (
          <div key={bar.key} style={box}>
            <label style={{ display: "flex", gap: "10px", alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" checked={!!checked[bar.key]} onChange={() => toggle(bar.key)} style={{ width: "20px", height: "20px", marginTop: "3px" }} />
              <span>
                <strong style={{ fontSize: "18px" }}>{bar.name}</strong>
                <span style={{ display: "block", color: "#B8B0C8", fontSize: "15px" }}>
                  {bar.address} · {bar.phone}
                </span>
                {m && (
                  <span style={{ display: "block", fontSize: "14px", color: m.placeId ? "#4ADE80" : "#FF6B8A" }}>
                    {m.placeId ? `Google match: ${m.name}, ${m.address}` : m.error}
                  </span>
                )}
                {bar.note && <span style={{ display: "block", fontSize: "14px", color: "#8A84A0", marginTop: "4px" }}>{bar.note}</span>}
              </span>
            </label>

            {(bar.events.length > 0 || bar.specials.length > 0) && (
              <div style={{ marginTop: "12px", paddingLeft: "30px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {bar.events.map((e, i) => (
                  <label key={`e${i}`} style={{ display: "flex", gap: "8px", fontSize: "15px", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!checked[`${bar.key}:e${i}`]} onChange={() => toggle(`${bar.key}:e${i}`)} />
                    <span>
                      <strong>{e.title}</strong> — {whenEvent(e)}{" "}
                      <a href={e.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#B9A6FF" }}>source</a>
                    </span>
                  </label>
                ))}
                {bar.specials.map((s, i) => (
                  <label key={`s${i}`} style={{ display: "flex", gap: "8px", fontSize: "15px", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!checked[`${bar.key}:s${i}`]} onChange={() => toggle(`${bar.key}:s${i}`)} />
                    <span>
                      <strong>{s.title}</strong> — {whenSpecial(s)}{" "}
                      <a href={s.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#B9A6FF" }}>source</a>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
